import { getClient } from '@/api/api-client';
import {
    AnnualServiceChargeStatement,
    type ExportMonthlyInvestorShareResult,
    type IAnnualServiceChargeStatement,
    type IBaseInvestorYearlyPayout,
    type IDocument,
    type IDurableJob,
    type IEstateCredential,
    type IEstateMeter,
    type IEstatesResponse,
    type IFetchRentTenancyOverview,
    type IGenerateRentArrearNoticeParams,
    type IInvestorInfo,
    type IInvestorYearlyPayout,
    type IMeterReading,
    type IMonthlyRentGuarantee,
    type IPropertyManagementReqQuery,
    type IRent,
    type IRentTenancy,
    type IRentTenancyDTO,
    type IRentingLead,
    InvestorInfo,
    InvestorYearlyPayout,
    RentTenancy,
    type StringDate,
} from '@condo/domain';
import type {
    BaseAnnualServiceChargeStatement,
    CreateInvestorInfoMutationRequest,
    CreateInvestorYearlyPayoutMutationRequest,
    CreateInvoiceFromBkaMutationResponse,
    CreateRentMutationRequest,
    ExportMonthlySevPaymentMutationRequest,
    ExportMonthlySevPaymentMutationResponse,
    ExportMonthlySevPaymentQueryParams,
    FindAnnualServiceChargeStatementsQueryParams,
    FindAvailableDraftsForRentalContractQueryParams,
    FindInvestorAnnualSettlementsQueryParams,
    FindInvestorInfosQueryParams,
    FindInvestorYearlyPayoutsQueryParams,
    FindMonthlyRentGuaranteesQueryParams,
    FindMonthlyRentGuaranteesQueryResponse,
    GenerateInvestorYearlyPayoutMutationRequest,
    GetAnnualServiceChargeStatementPathParams,
    GetAnnualServiceChargeStatementQueryParams,
    GetAnnualServiceChargeStatementQueryResponse,
    GetAnnualServiceChargeStatementsByRentalContractQueryParams,
    GetAnnualServiceChargeStatementsByTenancyQueryParams,
    GetCpiValuesListQueryResponse,
    GetEstateRentTenanciesQueryParams,
    GetInvestorInfoQueryParams,
    GetInvestorYearlyPayoutQueryParams,
    GetMinMaxDateOfMonthlyInvestorShareQueryResponse,
    GetRentTenancyQueryParams,
    InsertableInvestorAnnualSettlementSchema,
    InvestorAnnualSettlement,
    PatchAnnualServiceChargeStatementMutationRequest,
    PortalName,
    SendEmailInvestorYearlyPayoutPathParams,
    UpdatableInvestorAnnualSettlementSchema,
    UpdateInvestorInfoMutationRequest,
    UpdateInvestorYearlyPayoutMutationRequest,
    UpdateMonthlyInvestorShareMutationRequest,
    UpdateMonthlyInvestorShareMutationResponse,
    UpdateMonthlyInvestorShareOperationsMutationRequest,
    UpdateRentMutationRequest,
} from '@condo/sdk/propx';

export type IFetchRentTenanciesOverviewParams = IFetchRentTenancyOverview['findOpts'] & IFetchRentTenancyOverview['queryOpts'];

export const findPropertyManagementEstates = async (params?: IPropertyManagementReqQuery): Promise<IEstatesResponse> =>
    getClient('basecamp')
        .get('/estates/property-management', { params })
        .then(response => response.data);

export const fetchEstateMeters = async (estateId: number): Promise<IEstateMeter[]> =>
    getClient('basecamp')
        .get(`/estates/${estateId}/estate-meters`)
        .then(r => r.data.estateMeters);

export const saveEstateMeter = async (meter: Partial<IEstateMeter>): Promise<IEstateMeter> => {
    const { estateId } = meter;
    return getClient('basecamp')
        .post(`/estates/${estateId}/estate-meters`, meter)
        .then(r => r.data.meter);
};

export const saveEstateReading = async (estateMeterId: number, reading: IMeterReading): Promise<IMeterReading> =>
    getClient('basecamp')
        .post(`/estate-meters/${estateMeterId}/readings`, reading)
        .then(r => r.data.meter);

export const deleteMeter = async (estateMeterId: number): Promise<void> => getClient('basecamp').delete(`/estate-meters/${estateMeterId}`);

export const deleteMeterReading = async (estateMeterId: number, readingId: number): Promise<void> =>
    getClient('basecamp')
        .delete(`/estate-meters/${estateMeterId}/readings/${readingId}`)
        .then(r => r.data);

// =================== investor infos ===================

export const saveInvestorInfo = async (investorInfoDto: CreateInvestorInfoMutationRequest | UpdateInvestorInfoMutationRequest): Promise<InvestorInfo> => {
    if (investorInfoDto.investorInfoId) {
        const data = await getClient('propx').updateInvestorInfo({ investorInfoId: investorInfoDto.investorInfoId }, investorInfoDto);
        return new InvestorInfo(data.investorInfo);
    }
    const data = await getClient('propx').createInvestorInfo(investorInfoDto as CreateInvestorInfoMutationRequest);
    return new InvestorInfo(data.investorInfo);
};

export const findInvestorInfos = async (query?: { estateId?: number }, opts?: FindInvestorInfosQueryParams['opts']): Promise<InvestorInfo[]> =>
    getClient('propx')
        .findInvestorInfos({ ...query, opts })
        .then(({ investorInfos }) => {
            return investorInfos.map(iinfo => new InvestorInfo(iinfo as IInvestorInfo));
        });

export const getInvestorInfo = async (investorInfoId: number, opts?: GetInvestorInfoQueryParams): Promise<InvestorInfo | null> => {
    if (!investorInfoId) {
        return null;
    }
    const data = await getClient('propx').getInvestorInfo({ investorInfoId }, opts);
    return new InvestorInfo(data.investorInfo);
};

// =================== rent tenancy ===================

export const saveTenancy = async (rentTenancy: IRentTenancyDTO, investorInfo?: Partial<IInvestorInfo>, rent?: Partial<IRent>): Promise<RentTenancy> => {
    if (rentTenancy.rentTenancyId) {
        const data = await getClient('propx').updateRentTenancy({ rentTenancyId: rentTenancy.rentTenancyId }, rentTenancy);
        return new RentTenancy(data.rentTenancy);
    }
    const data = await getClient('propx').saveRentTenancy({ rentTenancy, investorInfo, rent });
    return new RentTenancy(data.rentTenancy);
};

export const fetchRentTenancy = async (rentTenancyId: number, params?: GetRentTenancyQueryParams): Promise<RentTenancy> => {
    const data = await getClient('propx').getRentTenancy({ rentTenancyId }, params);
    return new RentTenancy(data as IRentTenancy);
};

export const fetchRentTenancyOverview = async (params: IFetchRentTenanciesOverviewParams): Promise<IFetchRentTenancyOverview> => {
    const { data } = await getClient('basecamp').get<IFetchRentTenancyOverview>(`/rent-tenancies`, { params });
    return {
        rentTenancies: data.rentTenancies.map((rt: IRentTenancy) => new RentTenancy(rt)),
        count: data.count,
    };
};

export const fetchRentalContractOverview = async (
    params: IFetchRentTenanciesOverviewParams,
): Promise<{
    rentTenancies: RentTenancy[];
    count: number;
}> => {
    const { data } = await getClient('basecamp').get<{ rentTenancies: IRentTenancy[]; count: number }>(`/rental-contracts`, { params });
    return { rentTenancies: data.rentTenancies.map((rt: IRentTenancy) => new RentTenancy(rt)), count: data.count };
};

export const fetchRentTenanciesOfEstate = async (estateId: number, params?: GetEstateRentTenanciesQueryParams): Promise<RentTenancy[]> => {
    const data = await getClient('propx').getEstateRentTenancies({ estateId }, params);
    return data.rentTenancies.map(rt => new RentTenancy(rt as IRentTenancy)).sort((a, b) => b.rentTenancyId - a.rentTenancyId);
};

export const fetchAvailableDrafts = async (
    params: FindAvailableDraftsForRentalContractQueryParams,
): Promise<Array<IDocument & { rentingLead: IRentingLead }>> => {
    const data = await getClient('propx').findAvailableDraftsForRentalContract(params);
    return data as Array<IDocument & { rentingLead: IRentingLead }>;
};

export const recalculateRentBalance = async (rentTenancyId: number): Promise<RentTenancy> => {
    const data = await getClient('propx').recalculateRentBalance({ rentTenancyId });
    return new RentTenancy(data.rentTenancy as IRentTenancy);
};

export const fetchActiveRentTenanciesOfManyEstate = async (estateIds: number[]): Promise<RentTenancy[]> => {
    const data = await getClient('propx').getManyEstateActiveRentTenancies({ estateIds });
    return data.rentTenancies.map(rt => new RentTenancy(rt as IRentTenancy));
};

// =================== NebenkostenAbrechnung ===================
export const fetchAnnualServiceChargeStatementsByTenancy = async (
    params: GetAnnualServiceChargeStatementsByTenancyQueryParams,
): Promise<AnnualServiceChargeStatement[]> => {
    const { annualServiceChargesStatements } = await getClient('propx').getAnnualServiceChargeStatementsByTenancy(params);
    return annualServiceChargesStatements.map(s => new AnnualServiceChargeStatement(s as IAnnualServiceChargeStatement));
};

export const fetchAnnualServiceChargeStatementsByContract = async (
    params: GetAnnualServiceChargeStatementsByRentalContractQueryParams,
): Promise<AnnualServiceChargeStatement[]> => {
    const { annualServiceChargesStatements } = await getClient('propx').getAnnualServiceChargeStatementsByRentalContract(params);
    return annualServiceChargesStatements.map(s => new AnnualServiceChargeStatement(s as IAnnualServiceChargeStatement));
};

export const findAnnualServiceChargeStatements = async (
    query: FindAnnualServiceChargeStatementsQueryParams,
): Promise<{
    serviceChargeStatements: AnnualServiceChargeStatement[];
    count: number;
}> => {
    const { annualServiceChargesStatements, count } = await getClient('propx').findAnnualServiceChargeStatements(query);
    return { serviceChargeStatements: annualServiceChargesStatements.map(s => new AnnualServiceChargeStatement(s as IAnnualServiceChargeStatement)), count };
};

export const fetchAnnualServiceChargeStatementsByEstate = async (estateId: number): Promise<AnnualServiceChargeStatement[]> => {
    const annualServiceChargesStatements = await getClient('propx').getAnnualServiceChargeStatementsByEstate({ estateId });
    return annualServiceChargesStatements.map(s => new AnnualServiceChargeStatement(s as IAnnualServiceChargeStatement));
};

export const getAnnualServiceChargeStatement = async (
    params: GetAnnualServiceChargeStatementPathParams,
    queryParams?: GetAnnualServiceChargeStatementQueryParams,
): Promise<GetAnnualServiceChargeStatementQueryResponse> => {
    return getClient('propx').getAnnualServiceChargeStatement(params, queryParams);
};

export const deleteAnnualServiceCharge = async (serviceChargeStatementId: number): Promise<void> =>
    getClient('propx').deleteAnnualServiceChargeStatement({ serviceChargeStatementId });

export type SaveAnnualServiceChargeStatementInput = Partial<BaseAnnualServiceChargeStatement> &
    (Pick<BaseAnnualServiceChargeStatement, 'amount' | 'year' | 'rentalContractId'> | { serviceChargeStatementId: number });
export const saveAnnualServiceChargeStatement = async (data: SaveAnnualServiceChargeStatementInput) =>
    getClient('propx').saveAnnualServiceChargeStatement({ annualServiceChargeStatement: data as BaseAnnualServiceChargeStatement });

export const patchAnnualServiceChargeStatement = async (serviceChargeStatementId: number, data: PatchAnnualServiceChargeStatementMutationRequest) =>
    getClient('propx').patchAnnualServiceChargeStatement({ serviceChargeStatementId }, data);

export const createInvoiceFromBka = async (serviceChargeStatementId: number, documentId: number): Promise<CreateInvoiceFromBkaMutationResponse> =>
    getClient('propx').createInvoiceFromBka({ serviceChargeStatementId }, { documentId });

// =================== rent ===================

export const saveRent = async (rent: IRent): Promise<IRent> => {
    if (rent.rentId) {
        const data = await getClient('propx').updateRent({ rentId: rent.rentId }, { rent } as UpdateRentMutationRequest);
        return data.rent;
    }
    const data = await getClient('propx').createRent({ rent } as CreateRentMutationRequest);
    return data.rent;
};

export const getCpiValuesList = async (startDate?: Date, endDate?: Date): Promise<GetCpiValuesListQueryResponse> => {
    return getClient('propx').getCpiValuesList({ startDate, endDate });
};

export const fetchMonthlyRentGuarantees = async (params?: FindMonthlyRentGuaranteesQueryParams): Promise<FindMonthlyRentGuaranteesQueryResponse> =>
    getClient('propx').findMonthlyRentGuarantees(params);

export const updateMonthlyRentGuarantee = async (monthlyRentGuaranteeId: number, data: Partial<IMonthlyRentGuarantee>): Promise<IMonthlyRentGuarantee> =>
    getClient('propx')
        .updateMonthlyRentGuarantee({ monthlyRentGuaranteeId }, data)
        .then(({ monthlyRentGuarantee }) => monthlyRentGuarantee as IMonthlyRentGuarantee);

export const getDateRangeOfMonthlyInvestorShare = async (): Promise<GetMinMaxDateOfMonthlyInvestorShareQueryResponse> => {
    return getClient('propx').getMinMaxDateOfMonthlyInvestorShare();
};

export const getMonthlyInvestorSharePaymentFile = async (query?: { month: StringDate }): Promise<ExportMonthlyInvestorShareResult> => {
    const { data } = await getClient('basecamp').get('/investor-share/payment-file', {
        params: query,
    });

    return data;
};

export const getSevPaymentFile = async (
    data: ExportMonthlySevPaymentMutationRequest,
    query: ExportMonthlySevPaymentQueryParams,
): Promise<ExportMonthlySevPaymentMutationResponse> => getClient('propx').exportMonthlySevPayment(data, { month: query?.month });

export const updateMonthlyInvestorShare = async (
    monthlyInvestorShareId: number,
    monthlyInvestorShare?: UpdateMonthlyInvestorShareMutationRequest['data'],
    operationRows?: UpdateMonthlyInvestorShareMutationRequest['operationRows'],
): Promise<UpdateMonthlyInvestorShareMutationResponse['monthlyInvestorShare']> =>
    getClient('propx')
        .updateMonthlyInvestorShare(
            { monthlyInvestorShareId },
            {
                operationRows,
                data: monthlyInvestorShare,
            },
        )
        .then(data => data.monthlyInvestorShare);

export const updateMonthlyInvestorShareOperations = async (monthlyInvestorShareId: number, data: UpdateMonthlyInvestorShareOperationsMutationRequest) =>
    getClient('propx').updateMonthlyInvestorShareOperations({ monthlyInvestorShareId }, data);

/**
 *
 * @param month in "date" format 'YYYY-MM-DD'
 */
export const generateRentGuarantee = async (month: string): Promise<string> =>
    getClient('propx')
        .rentGuaranteeBatchGenerate({ month: month })
        .then(({ driveFolderId }) => driveFolderId);

export const generateRentArrearNotices = async (rentArrearNotices: IGenerateRentArrearNoticeParams[]): Promise<string> =>
    getClient('propx')
        .generateArrearNotices({ rentArrearNotices })
        .then(({ driveFolderId }) => driveFolderId);

export const deleteRentArrearNotice = async (rentArrearNoticeId: number): Promise<void> => getClient('propx').deleteArrearNotice({ rentArrearNoticeId });

export const sendContactInvitationRequest = async ({ contactId, portalName }: { contactId: number; portalName: PortalName }): Promise<void> => {
    await getClient('propx').sendContactInvitation({ contactId }, { portalName });
};

export const fetchEstateCredentials = async (estateId: number): Promise<IEstateCredential[]> =>
    getClient('basecamp')
        .get<{ estateCredentials: IEstateCredential[] }>(`/estates/${estateId}/credentials`)
        .then(({ data }) => data.estateCredentials);

export const deleteEstateCredential = async (estateCredentialId: number): Promise<void> => {
    await getClient('basecamp').delete(`/estates/credentials/${estateCredentialId}`);
};

export const createEstateCredential = async (estateId: number, data: Partial<IEstateCredential>): Promise<IEstateCredential> =>
    getClient('basecamp')
        .post<{
            estateCredential: IEstateCredential;
        }>(`/estates/${estateId}/credentials`, data)
        .then(({ data }) => data.estateCredential);

export const getEntitySetting = async (
    estateId: number,
): Promise<{
    [key: string]: string | boolean;
} | null> => {
    const { entitySetting } = await getClient('propx').getEntitySetting({ estateId });

    return entitySetting?.value ?? null;
};

export const saveFinancialModuleSetting = async (estateId: number, value: boolean): Promise<void> => {
    await getClient('propx').upsertEntitySetting({ estateId, value: { disableFinancialModule: value } });
};

export const getInvestorPortalAccess = async (
    investorInfoId: number,
): Promise<{
    portalAccessDisabled: boolean;
    canToggle: boolean;
}> => getClient('propx').getInvestorPortalAccess({ investorInfoId });

export const setInvestorPortalAccess = async (investorInfoId: number, portalAccessDisabled: boolean): Promise<void> => {
    await getClient('propx').setInvestorPortalAccess({ investorInfoId }, { portalAccessDisabled });
};

export const restoreTenancyLedger = async (rentTenancyId: number): Promise<void> =>
    getClient('basecamp').put(`/rent-tenancies/${rentTenancyId}/restore-ledger`, {});

export const recoverInvestorLedgerAsync = async (investorInfoId: number): Promise<IDurableJob> =>
    getClient('basecamp').post(`/investor-infos/${investorInfoId}/async-recover-ledger`, {});

export const recoverInvestorLedger = async (investorInfoId: number): Promise<void> =>
    getClient('basecamp').post(`/investor-infos/${investorInfoId}/recover-ledger`, {});

export const restoreAssetLedger = async (mfhId: number): Promise<void> => {
    await getClient('basecamp').post(`/mfh/${mfhId}/restore-ledger`, {});
};

/**
 * InvestorAnnualSettlements
 */
export const fetchInvestorInvestorAnnualSettlements = async (params: FindInvestorAnnualSettlementsQueryParams): Promise<InvestorAnnualSettlement[]> => {
    const { investorAnnualSettlements } = await getClient('propx').findInvestorAnnualSettlements(params);
    return investorAnnualSettlements;
};

export const createInvestorInvestorAnnualSettlement = async (data: InsertableInvestorAnnualSettlementSchema): Promise<InvestorAnnualSettlement> => {
    const { investorAnnualSettlement } = await getClient('propx').createInvestorAnnualSettlement({ investorAnnualSettlement: data });
    return investorAnnualSettlement;
};

export const deleteInvestorInvestorAnnualSettlement = async (investorAnnualSettlementId: number): Promise<void> => {
    await getClient('propx').deleteInvestorAnnualSettlement({ investorAnnualSettlementId });
};

export const updateInvestorInvestorAnnualSettlement = async (data: UpdatableInvestorAnnualSettlementSchema): Promise<InvestorAnnualSettlement> => {
    const { investorAnnualSettlement } = await getClient('propx').updateInvestorAnnualSettlement({ investorAnnualSettlement: data });
    return investorAnnualSettlement;
};

/**
 * InvestorYearlyPayouts
 */
export const fetchInvestorYearlyPayouts = async (params: FindInvestorYearlyPayoutsQueryParams): Promise<InvestorYearlyPayout[]> => {
    const { investorYearlyPayouts } = await getClient('propx').findInvestorYearlyPayouts(params);
    return investorYearlyPayouts.map(i => new InvestorYearlyPayout(i as IInvestorYearlyPayout));
};

export const createInvestorYearlyPayout = async (data: CreateInvestorYearlyPayoutMutationRequest['investorYearlyPayout']): Promise<InvestorYearlyPayout> => {
    const { investorYearlyPayout } = await getClient('propx').createInvestorYearlyPayout({ investorYearlyPayout: data });
    return new InvestorYearlyPayout(investorYearlyPayout as IInvestorYearlyPayout);
};

export const generateInvestorYearlyPayout = async (data: GenerateInvestorYearlyPayoutMutationRequest): Promise<IBaseInvestorYearlyPayout> => {
    const { investorYearlyPayout } = await getClient('propx').generateInvestorYearlyPayout(data);
    return investorYearlyPayout as IBaseInvestorYearlyPayout;
};

export const deleteInvestorYearlyPayout = async (investorYearlyPayoutId: number): Promise<void> => {
    await getClient('propx').deleteInvestorYearlyPayout({ investorYearlyPayoutId });
};

export const updateInvestorYearlyPayout = async (data: UpdateInvestorYearlyPayoutMutationRequest['investorYearlyPayout']): Promise<InvestorYearlyPayout> => {
    const { investorYearlyPayout } = await getClient('propx').updateInvestorYearlyPayout({ investorYearlyPayout: data });
    return new InvestorYearlyPayout(investorYearlyPayout as IInvestorYearlyPayout);
};
export const sendEmailInvestorYearlyPayout = async (
    investorYearlyPayoutId: SendEmailInvestorYearlyPayoutPathParams['investorYearlyPayoutId'],
): Promise<void> => {
    return getClient('propx').sendEmailInvestorYearlyPayout({ investorYearlyPayoutId });
};

export const getInvestorYearlyPayout = async (
    investorYearlyPayoutId: number,
    opts?: GetInvestorYearlyPayoutQueryParams,
): Promise<InvestorYearlyPayout | null> => {
    const { investorYearlyPayout } = await getClient('propx').getInvestorYearlyPayout({ investorYearlyPayoutId }, opts);
    return investorYearlyPayout ? new InvestorYearlyPayout(investorYearlyPayout as IInvestorYearlyPayout) : null;
};

export const generateAndSendInvestorPayoutStatement = async (month: Date): Promise<void> => {
    await getClient('propx').generateAndSendMonthlyInvestorShares({ month });
};

export const runInvestorShareStatementGeneration = async (month: Date): Promise<void> => {
    await getClient('propx').generateInvestorPayoutDocument();
};

export const runInvestorShareStatementSendout = async (month: Date): Promise<void> => {
    await getClient('propx').sendInvestorPayoutDocument();
};

// ============================ Cache ============================

export const resetCache = async (opts: { cacheKeys?: string[]; kind: 'repo' | 'usecase'; prefix?: string }): Promise<void> =>
    getClient('propx').resetCache({ cacheKeys: opts.cacheKeys, kind: opts.kind });

// =================== rent ===================

export const getMfhLatestRents = async (mfhId: number) => getClient('propx').getMfhLatestRents({ mfhId });
